import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereCircularProgress } from "@components/common/sphere-circular-progress";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { ReactSetStateFunction } from "@custom-types/types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Typography } from "@mui/material";
import { blue, neutral, DeleteIcon } from "@flat-ui";
import { FILE_SIZE_MULTIPLIER } from "@utils/file-utils";
import { ChangeEvent, useMemo } from "react";
import defaultUploadImage from "/src/assets/illustrations/upload-image.webp";

interface Props {
  /** Avatar component of user. If provided existingImageUrl will be ignored */
  avatar?: JSX.Element;

  /** Progress value to show inside the spinner */
  uploadProgress: number;

  /** Existing url of an image to display inside the dropzone */
  existingImageUrl?: string;

  /** True if logo uploading is in progress */
  isLoading: boolean;

  /** Callback setState function to set the input element */
  setFileInputEl: ReactSetStateFunction<HTMLInputElement | null>;

  /** Triggers when a file is selected from file manager dialog */
  selectFileFromDialog(e: ChangeEvent<HTMLInputElement>): void;

  /** Callback function which triggers on delete button click */
  onDeleteButtonClick?(): void;

  /** Text to show in delete icon tooltip */
  deleteIconTooltipText?: string;

  /** Whether the supported format should show in the dropzone */
  shouldShowSupportedFormats?: boolean;

  /** Whether the size limit should show in the dropzone */
  shouldShowSizeLimit?: boolean;

  /** List of file extensions that are allowed in the dropzone */
  allowedExtensions: string[];

  /** First part of instruction, can be different based on the usage of the dropzone */
  instruction?: string;

  /** Maximum file size the dropzone should allow to upload */
  maxFileSize: number;

  /** Whether the multiple files can be selected or not */
  shouldAllowMultiUpload: boolean;

  /** Whether the dropzone should handle files. Default: true. */
  shouldAllowFiles?: boolean;
}

/** CSS class name for the remove workspace logo button  */
export const REMOVE_WORKSPACE_LOGO = "removeWorkspaceLogo";

/** Renders the dropzone elements based on the loading state */
export function DropzoneElements({
  avatar,
  isLoading,
  uploadProgress,
  existingImageUrl,
  setFileInputEl,
  selectFileFromDialog,
  onDeleteButtonClick,
  deleteIconTooltipText,
  shouldShowSupportedFormats = true,
  shouldShowSizeLimit = true,
  allowedExtensions,
  instruction = "Drag your image here",
  maxFileSize,
  shouldAllowMultiUpload,
  shouldAllowFiles = true,
}: Props): JSX.Element {
  const { isHoverSupported } = useMediaQueryList();

  /** Contains the extensions for the accept attribute of input element  */
  const allowedExtensionsList = useMemo(() => {
    return allowedExtensions.map((extension) => `.${extension}`).join(", ");
  }, [allowedExtensions]);

  /** Show file size in GB if larger than or equal to 1024 MB */
  const maxFileSizeToShow = useMemo(() => {
    return maxFileSize >= FILE_SIZE_MULTIPLIER
      ? `${maxFileSize / FILE_SIZE_MULTIPLIER} GB`
      : `${maxFileSize} MB`;
  }, [maxFileSize]);

  /** returns the entity (file(s) or folder(s)) type to upload*/
  const entitiesToUploadStr = useMemo((): string => {
    // The browser's folder upload dialog does not allow multi-selection.
    if (shouldAllowMultiUpload && shouldAllowFiles) {
      return "files";
    } else {
      return shouldAllowFiles === false ? "folder" : "file";
    }
  }, [shouldAllowFiles, shouldAllowMultiUpload]);

  if (isLoading) {
    return (
      <SphereCircularProgress value={uploadProgress} label="Uploading..." />
    );
  }

  return (
    <>
      <Box
        component="div"
        sx={{
          height: "100px",
          width: "100px",
        }}
      >
        {/* Showing avatar if provided, otherwise existing or default image */}
        {avatar ? (
          avatar
        ) : (
          <img
            src={
              existingImageUrl?.length ? existingImageUrl : defaultUploadImage
            }
            style={{
              maxHeight: "100%",
              maxWidth: "100%",
            }}
            alt={
              existingImageUrl?.length
                ? "Your uploaded image"
                : "The default image indicates that an image can be uploaded here"
            }
          />
        )}
      </Box>

      {/* Instruction for upload a file */}
      <Box>
        <Box
          component="div"
          sx={{
            color: neutral[800],
            fontSize: "14px",
            marginBottom: "7px",
          }}
          data-testid="dropzone-title"
        >
          {instruction}, or{" "}
          <span
            style={{
              textDecoration: "underline",
              color: blue[500],
            }}
          >
            select {entitiesToUploadStr}
          </span>{" "}
          to upload
        </Box>

        {shouldShowSupportedFormats && (
          <Typography
            sx={{
              color: neutral[600],
              fontSize: "14px",
            }}
            data-testid="dropzone-sub-title"
          >
            Supported formats {allowedExtensionsList}
          </Typography>
        )}

        {shouldShowSizeLimit && (
          <Typography
            sx={{
              color: neutral[600],
              fontSize: "14px",
            }}
            data-testid="dropzone-sub-title-file-size"
          >
            Maximum file size: {maxFileSizeToShow}
          </Typography>
        )}
      </Box>
      {/* Input field to select file from dialog */}
      <input
        ref={setFileInputEl}
        type="file"
        style={{ display: "none" }}
        accept={allowedExtensionsList}
        onChange={selectFileFromDialog}
        multiple={shouldAllowMultiUpload}
        data-testid="dropzone-input"
      />

      {/* Delete icon is always shown if hovering is not supported. If hovering is supported, it is visible on dropzone hover */}
      {onDeleteButtonClick && existingImageUrl?.length !== 0 && (
        <Box
          className={REMOVE_WORKSPACE_LOGO}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            // Overridden by REMOVE_WORKSPACE_LOGO class
            visibility: isHoverSupported ? "hidden" : "visible",
          }}
        >
          <SphereTooltip title={deleteIconTooltipText}>
            <SphereIconButton
              component={DeleteIcon}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteButtonClick();
              }}
              iconSize="19px"
              buttonSize="40px"
            />
          </SphereTooltip>
        </Box>
      )}
    </>
  );
}
